import React from 'react';
import styled from 'styled-components';

import {
  darken,
  rgba,
} from '@savgroup-front-common/primitive/helpers/cssColor';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import {
  DefaultStyledProps,
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { styledGetCustomComponentProperty } from '../../../helpers';
import getBordersBasedOnProps from '../../../theme/helpers/getBordersBasedOnProps';
import {
  FIELD_STATUS,
  InputStatus,
} from '../common/helpers/getFinalFieldState.types';

interface ColorBasedOnProps extends DefaultStyledProps {
  $status: InputStatus;
}
const colorBasedOnProps = (props: ColorBasedOnProps) => {
  const { theme, $status } = props;

  if ($status === FIELD_STATUS.ERROR) {
    return theme.colors.danger;
  }
  if ($status === FIELD_STATUS.WARNING) {
    return theme.colors.alert;
  }
  if ($status === FIELD_STATUS.SUCCESS) {
    return theme.colors.success;
  }

  return theme.newUI.inputBorderColors.default;
};

const colorFocusBasedOnProps = (props: DefaultStyledProps) => {
  const { theme } = props;

  return theme.colors.primary;
};

interface StyledTextAreaProps extends DefaultStyledProps {
  $status: InputStatus;
  autoHeight?: boolean;
  $isInitialOneLine?: boolean;
  disabled: boolean;
  $isRequired: boolean;
  $isLoading: boolean;
  $hasIcon: boolean;
  $componentThemeName?: string;
}

export const $StyledTextArea = styled.textarea<StyledTextAreaProps>`
  outline: none;
  overflow-y: auto !important;

  padding-top: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.TEXT_AREA,
    property: SUPPORTED_PROPERTIES.PADDING_TOP,
    fallback: spacing('md'),
  })};
  padding-bottom: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.TEXT_AREA,
    property: SUPPORTED_PROPERTIES.PADDING_BOTTOM,
    fallback: spacing('md'),
  })};

  padding-left: ${({ $hasIcon }) =>
    $hasIcon
      ? spacing(9)
      : styledGetCustomComponentProperty({
          componentName: SUPPORTED_COMPONENTS.TEXT_AREA,
          property: SUPPORTED_PROPERTIES.PADDING_LEFT,
          fallback: spacing(3.5),
        })};
  padding-right: ${({ $isLoading }) =>
    $isLoading
      ? spacing(9)
      : styledGetCustomComponentProperty({
          componentName: SUPPORTED_COMPONENTS.TEXT_AREA,
          property: SUPPORTED_PROPERTIES.PADDING_RIGHT,
          fallback: spacing(3.5),
        })};
  border-style: solid;
  border-radius: ${(props) => props.theme.newUI.borders.mediumRadius};
  border-width: ${(props) => getBordersBasedOnProps(props).thickness};
  border-color: ${(props) => colorBasedOnProps(props)};
  width: 100%;
  height: ${({ $isInitialOneLine }) => {
    return $isInitialOneLine ? spacing(10) : null;
  }};
  min-height: ${({ $isInitialOneLine, $componentThemeName }) => {
    if ($isInitialOneLine) {
      return styledGetCustomComponentProperty({
        componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
        property: SUPPORTED_PROPERTIES.HEIGHT,
      });
    }

    if ($componentThemeName) {
      return styledGetCustomComponentProperty({
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.HEIGHT,
      });
    }

    return spacing(32);
  }};
  max-height: ${spacing(40)};
  resize: none;
  line-height: normal;
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  &:focus,
  &&&:focus,
  &:hover {
    border-color: ${(props) =>
      props.disabled || props.$status === FIELD_STATUS.ERROR
        ? null
        : colorFocusBasedOnProps(props)};
    box-shadow: ${({ theme }) =>
      `0px 0px 4px ${rgba(theme.colors.primary, 0.4)}`};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.paragraphTextColor};
  }

  background-color: ${({ disabled, theme }) =>
    disabled ? theme.colors.disabled : theme.systemColors.defaultBackground};
  color: ${({ disabled, theme }) =>
    disabled ? darken(0.3, theme.colors.default) : theme.colors.mainTextColor};
  resize: ${({ autoHeight }) => (autoHeight ? 'none' : null)};
`;

export const $TextareaContainer = styled.div`
  position: relative;
  width: 100%;
  line-height: 0;
`;

export const $IconContainer = styled.div`
  position: absolute;
  top: 50%;
  left: ${spacing(2.5)};
  transform: translateY(-50%);
  height: ${spacing(5)};
`;

export const $CustomIcon = styled(({ icon, ...props }) =>
  React.cloneElement(icon, props),
)``;

export const $TextAreaFooter = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row-reverse;
`;

export const $RemainingCharactersStyled = styled.div`
  margin: 0 ${spacing('sm')};
`;

export const $SuffixBottomButton = styled.div`
  display: flex;
  height: ${spacing(5)};
  flex-direction: row;
  align-items: center;
  justify-content: end;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: ${spacing('px')};
`;
